/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Spinner,
  SvgIcon,
  ActivityTimelineEntry,
  IdentityName,
  SpanWithData
} from '../'

import {
  encodeBase64
} from '../../utils'

class ActivityTimelineEvent extends React.Component {
  static propTypes = {
    event: PropTypes.object.isRequired
  }

  insertAddCharacter = (updateActions) => {
    if (updateActions.length !== 0) {
      return <span>&nbsp;<b>&</b>&nbsp;</span>
    }
  }

  getActionText = (eventData) => {
    let event = this.props.event
    if (event.eventType === "model.created") {
      return "opened case."
    } else if (event.eventType === "model.updated") {
      if (eventData.status === "resolved") {
        return "resolved case."
      } else if (eventData.status === "open") {
        return "re-opened case."
      } else {
        let updateActions = []
        let actions = Object.keys(eventData).sort()
        actions.forEach((key, index) => {
          if (key === "assigned_to") {
            let assignee = <IdentityName id={encodeBase64(eventData["assigned_to"], "IdentityNode")} />
            updateActions.push(
              <div key='assignedTo'>
                reassigned case to <b>{assignee}</b>
              </div>
            )
          } else if (key === "next_action_at") {
            updateActions.push(
              <div key='nextActionAt'>
                {this.insertAddCharacter(updateActions)}
                {`set next action date to ${moment(eventData["next_action_at"]).format('lll')}`}
              </div>
            )
          } else if (key === "priority") {
            let priority = <SpanWithData
              forField="CasePriority"
              searchKey={eventData["priority"]}
            />
            updateActions.push(
              <div key='priority'>
                {this.insertAddCharacter(updateActions)}set priority to {priority}
              </div>
            )
          } else if (key === "product_type") {
            let productType = <SpanWithData
              forField="CaseProductType"
              searchKey={eventData["product_type"]}
            />
            updateActions.push(
              <div key='productType'>
                {this.insertAddCharacter(updateActions)}set product type to {productType}
              </div>
            )
          } else if (key === "request_type") {
            updateActions.push(
              <div key='requestType'>
                {this.insertAddCharacter(updateActions)}
                {`set the request type to ${eventData["request_type"]}`}
              </div>
            )
          }
        })
        // TODO: add other 'model.updated' event type mappings
        return updateActions
      }
    }
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className="slds-is-relative" size="small" />
    }

    if (this.props.data.error) {
      return (
        <div className="case-timeline-entry slds-grid slds-wrap slds-p-around_large slds-size_1-of-1">
          <div className="case-note-timestamp slds-m-right_large">
            {moment(this.props.event.createdAt).format("HH:mm")}
          </div>
          <div className="slds-size_1-of-12 slds-text-align_center">
            <SvgIcon
              variant="utility"
              symbol="warning"
              colour="error"
              size="small"
            />
          </div>
          <div className="slds-col slds-size_9-of-12">
            <div className="case-note-descriptor slds-size_1-of-1 slds-m-bottom_small"></div>
            <div className="case-note slds-size_10-of-12 slds-m-left_medium">
              Error loading event data.
            </div>
          </div>
        </div>
      )
    }

    let eventData = JSON.parse(this.props.event.eventData)
    let identity = eventData.hasOwnProperty("opened_by")
      ? this.props.data.identity
      : this.props.data.user.identity

    return (
      <ActivityTimelineEntry
        date={this.props.event.createdAt}
        identity={identity}
        identifier="event"
        action={this.getActionText(eventData)}
      />
    )
  }
}

const getIdentity = gql`
  query($id: ID!, $identityId: Boolean!) {
    identity(id: $id) @include(if: $identityId) {
      id
      firstName
      lastName
    }
    user(id: $id) @skip(if: $identityId) {
      id
      identity {
        id
        firstName
        lastName
      }
    }
  }
`

const withData = graphql(getIdentity, {
    options: ({ event }) => {
      let eventData = JSON.parse(event.eventData)
      let id = eventData.hasOwnProperty("opened_by")
        ? encodeBase64(eventData["opened_by"], "IdentityNode")
        : encodeBase64(eventData["updated_by"], "UserNode")  // eventData.updated_by stores user ids
      return {
        variables: {
          id,
          identityId: eventData.hasOwnProperty("opened_by")
        }
      }
    }
  })

export default withData(ActivityTimelineEvent)



// WEBPACK FOOTER //
// ./src/components/activity-timeline-event/index.js